import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase'; 
import { 
  ShieldAlert, Coins, FileText, Save, 
  ChevronLeft, User, HardDrive, Lock, 
  Zap, Database, ShieldCheck, Activity
} from 'lucide-react';

const MemberControlPage = () => {
  const { memberId } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchMember = async () => {
      const docRef = doc(db, "users", memberId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setMember(docSnap.data());
      setLoading(false);
    };
    fetchMember();
  }, [memberId]);

  const handleUpdate = async (updates) => {
    setUpdating(true);
    try {
      const docRef = doc(db, "users", memberId);
      await updateDoc(docRef, { ...updates, lastModified: serverTimestamp() });
      setMember(prev => ({ ...prev, ...updates }));
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return (
    <div className="h-screen w-full bg-slate-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 border-4 border-blue-900 border-t-blue-400 rounded-full animate-spin" />
        <span className="text-white font-black text-[10px] tracking-[0.3em] uppercase">Initializing Registry Node...</span>
      </div>
    </div>
  );

  return (
    <div className="h-screen w-full bg-white flex flex-col overflow-hidden">
      
      {/* 1. TOP NAV - FIXED */}
      <header className="h-20 bg-slate-900 text-white flex items-center justify-between px-8 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-8">
          <button onClick={() => window.history.back()} className="p-2 hover:bg-white/10 transition-all group">
            <ChevronLeft size={20} className="group-hover:text-blue-400" />
          </button>
          <div className="h-8 w-[1px] bg-white/20" />
          <div className="space-y-0.5">
            <h2 className="text-lg font-black uppercase tracking-tight leading-none">{member.fullName}</h2>
            <div className="flex items-center gap-3">
               <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">UID: {memberId}</span>
               <span className="h-1 w-1 rounded-full bg-blue-500" />
               <span className="text-[9px] text-blue-400 font-bold uppercase tracking-widest italic">Focus NDT Operational Node</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right hidden md:block">
            <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest">Network Authority</p>
            <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">CTO Level Access</p>
          </div>
          <div className={`px-5 py-2 text-[10px] font-black uppercase tracking-widest border-2 ${member.status === 'active' ? 'border-emerald-500 text-emerald-500 bg-emerald-500/5' : 'border-red-500 text-red-500 bg-red-500/5'}`}>
            {member.status === 'active' ? '● System Active' : '○ Access Revoked'}
          </div>
        </div>
      </header>

      {/* 2. MAIN CONTENT AREA - FULL WIDTH */}
      <main className="flex-1 flex overflow-hidden">
        
        {/* LEFT PANEL: CONFIGURATION (SCROLLABLE) */}
        <div className="flex-1 overflow-y-auto p-12 custom-scrollbar">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-16">
            
            {/* COLUMN 1: PERMISSIONS */}
            <div className="space-y-12">
              <section className="space-y-8">
                <h4 className="text-[11px] font-black uppercase tracking-[0.3em] text-blue-900 border-b-2 border-blue-900 pb-2 flex items-center gap-3">
                  <Lock size={14}/> 01. Authorization Matrix
                </h4>
                
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Corporate Role</label>
                    <select 
                      className="w-full bg-slate-50 p-5 font-bold text-sm outline-none border border-slate-200 focus:ring-1 focus:ring-blue-900 transition-all"
                      value={member.role}
                      onChange={(e) => handleUpdate({ role: e.target.value })}
                    >
                      <option>Operator</option>
                      <option>Supervisor</option>
                      <option>Auditor</option>
                      <option>Management</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Export Authority (Global)</label>
                    <select 
                      className="w-full bg-slate-50 p-5 font-bold text-sm outline-none border border-slate-200 focus:ring-1 focus:ring-amber-500 transition-all"
                      value={member.exportAuthority || "approval_required"}
                      onChange={(e) => handleUpdate({ exportAuthority: e.target.value })}
                    >
                      <option value="approval_required">SOP: Approval Required</option>
                      <option value="instant_access">SOP: Instant Bypass (Auto-Export)</option>
                      <option value="restricted">SOP: Data Lock (Restricted)</option>
                    </select>
                  </div>
                </div>
              </section>

              <section className="space-y-8 p-10 bg-slate-900 text-white relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-30 transition-opacity">
                    <Zap size={80} className="text-amber-400" />
                </div>
                
                <div className="relative z-10 space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Coins size={20} className="text-amber-400" />
                      <h3 className="text-xs font-black uppercase tracking-widest">Autonomous Credits</h3>
                    </div>
                    <span className="text-4xl font-black text-amber-400">{member.credits || 0}</span>
                  </div>

                  <div className="flex gap-4">
                    <button 
                      onClick={() => handleUpdate({ credits: (member.credits || 0) + 100 })}
                      className="flex-1 bg-white text-slate-950 p-4 text-[10px] font-black uppercase tracking-widest hover:bg-amber-400 transition-colors"
                    >
                      Add 100 Units
                    </button>
                    <button 
                      onClick={() => handleUpdate({ credits: 0 })}
                      className="flex-1 bg-transparent border border-white/20 text-white p-4 text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:border-red-600 transition-all"
                    >
                      Flush Credits
                    </button>
                  </div>
                </div>
              </section>
            </div>

            {/* COLUMN 2: TECHNICAL SPEC */}
            <div className="space-y-12">
               <section className="space-y-8">
                  <h4 className="text-[11px] font-black uppercase tracking-[0.3em] text-blue-900 border-b-2 border-blue-900 pb-2 flex items-center gap-3">
                    <ShieldCheck size={14}/> 02. NDT Level Validation
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cert Level</label>
                        <select 
                          className="w-full bg-slate-50 p-5 font-bold text-sm outline-none border border-slate-200"
                          value={member.ndtLevel}
                          onChange={(e) => handleUpdate({ ndtLevel: e.target.value })}
                        >
                          <option>Level I</option>
                          <option>Level II</option>
                          <option>Level III</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Primary Method</label>
                        <input 
                          type="text"
                          className="w-full bg-slate-50 p-5 font-bold text-sm outline-none border border-slate-200"
                          value={member.department || ""}
                          onChange={(e) => handleUpdate({ department: e.target.value })}
                        />
                    </div>
                  </div>
               </section>

               <section className="bg-slate-50 border border-slate-200 p-8 space-y-6">
                 <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                   <Activity size={14}/> Live Operational Log
                 </h4>
                 <div className="space-y-4">
                    <div className="flex justify-between items-center text-[10px] border-b border-slate-200 pb-3">
                        <span className="font-bold text-slate-500 uppercase">Registry Joined</span>
                        <span className="font-black text-slate-900">{member.createdAt?.toDate().toLocaleDateString() || "N/A"}</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] border-b border-slate-200 pb-3">
                        <span className="font-bold text-slate-500 uppercase">Last Sync Audit</span>
                        <span className="font-black text-slate-900">{member.lastModified?.toDate().toLocaleTimeString() || "Never"}</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px]">
                        <span className="font-bold text-slate-500 uppercase">Data Path</span>
                        <span className="font-black text-blue-900 uppercase">Firestore/Node-Alpha</span>
                    </div>
                 </div>
               </section>
            </div>
          </div>
        </div>

        {/* RIGHT SIDEBAR: SYSTEM ACTIONS (FIXED WIDTH) */}
        <aside className="w-[400px] bg-slate-50 border-l border-slate-200 p-10 flex flex-col shrink-0">
          <div className="space-y-8 flex-1">
            <div className="space-y-2">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">System Priority Actions</h4>
                <div className="h-1 w-10 bg-blue-900" />
            </div>

            <div className="space-y-4">
              <button 
                onClick={() => handleUpdate({ status: member.status === 'active' ? 'suspended' : 'active' })}
                className={`w-full p-5 text-[11px] font-black uppercase tracking-[0.2em] transition-all border-2 flex items-center justify-center gap-3 ${
                  member.status === 'active' ? 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white' : 'border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white'
                }`}
              >
                <ShieldAlert size={16} />
                {member.status === 'active' ? 'Suspend Access' : 'Re-Authorize Access'}
              </button>

              <button className="w-full p-5 bg-slate-900 text-white text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-slate-950 transition-all shadow-xl shadow-slate-200">
                <FileText size={16} />
                Export Profile PDF
              </button>
              
              <button className="w-full p-5 bg-white border border-slate-300 text-slate-900 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-slate-100 transition-all">
                <Database size={16} />
                Force Metadata Sync
              </button>
            </div>
          </div>

          {/* CRITICAL WARNING BOX */}
          <div className="mt-auto bg-white border-2 border-slate-900 p-6 space-y-4">
              <div className="flex items-center gap-2 text-red-600">
                  <ShieldAlert size={20} />
                  <span className="text-[11px] font-black uppercase tracking-widest underline">Strict Protocol</span>
              </div>
              <p className="text-[9px] font-bold uppercase leading-relaxed text-slate-600">
                Any adjustments to 'Export Authority' or 'NDT Level' recorded here are timestamped. Focus NDT Group holds full liability for compliance errors resulting from unauthorized registry modification.
              </p>
          </div>
        </aside>

      </main>

      {/* 3. STATUS FOOTER */}
      <footer className="h-10 bg-slate-100 border-t border-slate-200 px-8 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-6">
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">System Node: IN-MUM-01</span>
            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest italic">RSA-4096 Encrypted</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[8px] font-black text-emerald-600 uppercase tracking-widest">Registry Online</span>
          </div>
      </footer>
    </div>
  );
};

export default MemberControlPage;